/**
 * Entries compound tool — the WP-484 "entries family": list, get, batch,
 * search, update, commit, history, move, verify. Consolidates list-entries,
 * get-entry, batch-get, search (original) plus update-entry, commit-entry,
 * get-history, move-entry, verify-entry (WP-484 S1) into one tool, per the
 * two-layer shape (§3/§4). BR-72: Compound Tool Standard.
 *
 * The legacy standalone tools (update-entry, commit-entry, get-history,
 * move-entry, verify-entry) stay registered alongside `entries` until S3
 * deletes them (§7's "alongside" mechanic) — both call the SAME handler
 * functions (exported from knowledge.ts / verify.ts), so there is exactly
 * one business-logic path per action, not two to keep in sync.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, runWithToolContext, getWorkspaceId } from "../client.js";
import { success, failure, thinWrapper, parseOrFail, notFoundResult, type ToolResult } from "../envelope.js";
import { extractPreview, deriveEpistemicStatus, formatEpistemicLine, toEpistemicInput } from "./knowledge-helpers.js";
import { trackKnowledgeGap } from "../analytics.js";
import { recordGap } from "../gap-store.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { updateEntrySchema, commitEntrySchema, getHistorySchema, handleUpdateEntry, handleCommitEntry, handleGetHistory } from "./knowledge.js";
import { verifyEntrySchema, handleVerifyEntry } from "./verify.js";
import { handleMoveEntry, moveEntrySchema } from "./entry-move.js";
export { moveEntrySchema } from "./entry-move.js";

function sanitizeEntryData(data: unknown): Record<string, unknown> | undefined {
  if (!data || typeof data !== "object") return undefined;
  const filtered = Object.fromEntries(
    Object.entries(data as Record<string, unknown>).filter(([key]) => !key.startsWith("_")),
  );
  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

/**
 * TEN-2191 (honesty patch): build the self-announcing WHY line for a `get` result.
 *
 * whyQuality is rationale-only (the kernel dropped the description fallback), so a
 * missing or restated rationale is surfaced rather than masked:
 *   - 'missing'  → "**Why:** — (no rationale captured)"  (no `why` text exists)
 *   - 'restated' → "**Why:** <text> (restated)"
 *   - else (real rationale, or legacy entry carrying only `why`) → "**Why:** <text>"
 *
 * Returns the line (or null when there is nothing to render), the `why` text
 * (normalized to a string, "" when absent) for reuse in the structured payload,
 * and the data key whose value is now surfaced as Why so the caller can suppress
 * it from both the text and structured data blocks (STD-218 parity, Codex finding).
 *
 * whySourceKey reflects the kernel-resolved WHY field (rationale or description):
 *   - Prefers `e.whyField` ('rationale' | 'description') when present AND the
 *     corresponding data field is a non-empty string — uses it directly as the
 *     source key. Threaded from the kernel so arch-note `description` is recognized.
 *   - Falls back to rationale-only detection when `whyField` is absent (older
 *     kernels / non-governance entries) so behaviour is unchanged for those entries.
 *
 * Pure — mirrors the CLI `pb get` render. Exported for testing.
 */
export function renderWhyLine(e: Record<string, unknown>): {
  line: string | null;
  why: string;
  whySourceKey: string | null;
} {
  const why = typeof e.why === "string" ? e.why : "";
  if (e.whyQuality === "missing") {
    return { line: `**Why:** — (no rationale captured)`, why, whySourceKey: null };
  }
  if (why.length > 0) {
    const suffix = e.whyQuality === "restated" ? " (restated)" : "";
    const d = (e.data ?? {}) as Record<string, unknown>;
    // Prefer the kernel-threaded whyField (covers 'description' for arch notes).
    // Fall back to rationale-detection for older kernels that don't thread it.
    let whySourceKey: string | null = null;
    const kf = e.whyField;
    if ((kf === "rationale" || kf === "description") && typeof d[kf] === "string" && (d[kf] as string).trim().length > 0) {
      whySourceKey = kf;
    } else if (typeof d.rationale === "string" && d.rationale.trim().length > 0) {
      whySourceKey = "rationale";
    }
    return { line: `**Why:** ${why}${suffix}`, why, whySourceKey };
  }
  return { line: null, why, whySourceKey: null };
}

/**
 * Attestation-model finding (PR #341 review): render the honest `**Verification:**`
 * line the same way packages/cli/src/formatters/entry.ts renders it — connectors NEVER
 * re-derive strength (spec §5), they only display what chain.getEntry ships
 * (`verifiedBy` + `attestation.{strength,basis}`). A non-'attested' basis (the
 * pre-branch legacy corpus) is a legacy-honest label rendered as a bare qualifier
 * `(<verifiedBy>)`, never `— by <legacy string>`. Pure — exported for testing.
 */
export function renderVerificationLine(e: Record<string, unknown>): string | null {
  if (!e.verificationStatus) return null;
  const verifiedBy = typeof e.verifiedBy === "string" ? e.verifiedBy : undefined;
  const attestation = e.attestation && typeof e.attestation === "object"
    ? (e.attestation as { strength?: unknown; basis?: unknown })
    : undefined;
  const strength = attestation && typeof attestation.strength === "string" ? attestation.strength : undefined;
  const basis = attestation && typeof attestation.basis === "string" ? attestation.basis : undefined;

  let who: string;
  if (basis && basis !== "attested") {
    who = verifiedBy ? ` (${verifiedBy})` : "";
  } else {
    who = verifiedBy ? ` — by ${verifiedBy}${strength ? ` (${strength})` : ""}` : "";
  }
  return `**Verification:** ${String(e.verificationStatus)}${who}`;
}

// WP-484 S1 (§3/§4): the "entries family" — entries absorbs update-entry,
// commit-entry, get-history, move-entry, verify-entry as actions. The legacy
// standalone tools stay registered too (the "alongside until S3" mechanic,
// §7) — registerEntriesTools below only ADDS the new actions to `entries`.
const ENTRIES_ACTIONS = [
  "list", "get", "batch", "search", "update", "commit", "history", "move", "verify",
] as const;
type EntriesAction = (typeof ENTRIES_ACTIONS)[number];

const entriesStatusFilterSchema = z.union([z.string().max(200), z.array(z.enum(["draft", "active", "accepted", "deprecated", "archived"])).max(5)]); // PR #660 R1 Zqh1K/O: mirrors convex's max-5 + vocab bound, rejected before the round-trip.
const coherencyAcknowledgementFlatSchema = z.object({
  response: z.enum(['linked', 'accepted-fix', 'diverged']).describe("Explicit response to an acknowledge-required coherency challenge."),
  entryId: z.string().max(200).optional().describe("Authorizing entry being linked/accepted (required for 'linked' and 'accepted-fix')."),
  reason: z.string().max(2000).optional().describe("Divergence rationale (required for 'diverged', ≥12 chars)."),
});

/**
 * §3a — the REGISTERED layer: a flat ZodObject, action enum + the superset of
 * every absorbed action's optional params. Every param whose consuming
 * actions changed with this migration carries a description naming them
 * (§4 division of labor / §4 entries-merge worked example: `status` is a
 * `list`/`search` FILTER string here — the strict lifecycle union only
 * applies inside the `update` branch of the handler-layer discriminatedUnion,
 * §3b, below). TEN-1146 string length caps applied per-noun as each noun
 * migrates — entries is first, so free-text fields get caps here.
 */
export const entriesSchema = z.object({
  action: z.enum(ENTRIES_ACTIONS).describe(
    "'list': browse entries with filters. 'get': fetch one entry by ID. 'batch': fetch multiple entries. " +
      "'search': full-text search. 'update': change fields on an existing entry (draft by default). " +
      "'commit': accept a draft entry onto the Chain. 'history': audit trail for an entry. " +
      "'move': reclassify an entry to a different collection. 'verify': mark an entry as verified " +
      "(lightweight — no codebase scan; see `quality action=verify-chain` for the codebase-scanning check).",
  ),
  entryId: z.string().max(200).optional().describe(
    "Entry ID, e.g. '<PREFIX>-<n>'. Required for: get, update, commit, history, move, verify.",
  ),
  entryIds: z.array(z.string().max(200)).min(1).max(20).optional()
    .describe("Entry IDs for 'batch', e.g. ['TYPE-strategy', 'STR-jljeg7']"),
  collection: z.string().max(200).optional()
    .describe("Collection slug — for 'list'/'search': scope filter, e.g. 'glossary', 'tracking-events'."),
  status: entriesStatusFilterSchema.optional() // 'active'/'accepted' are the same trust level (DEC-1575) — both enumerated, never one substituted for the other.
    .describe(
      "For 'list': one lifecycle value (draft|active|accepted|deprecated|archived; active/accepted are the same trust level). " +
        "For 'search': one value OR an array of up to 5 (e.g. ['active','accepted']). For 'update': draft|active|deprecated|archived " +
        "ONLY — 'accepted' is set via 'commit', never 'update'. Workflow values use `workflowStatus` instead."),
  tag: z.string().max(200).optional().describe("For 'list': filter by internal tag."),
  label: z.string().max(200).optional().describe("For 'list': filter by label slug — matches entries across all collections."),
  query: z.string().min(2).max(500).optional().describe("For 'search': search text (min 2 characters)."),
  name: z.string().max(500).optional().describe("For 'update': new display name."),
  workflowStatus: z.string().max(200).optional().describe(
    "For 'update': collection workflow state. Valid values are collection-specific and server-owned — " +
      "discover via `collections action=describe`. Invalid values are rejected with the valid set.",
  ),
  data: z.record(z.unknown()).optional().describe("For 'update': fields to update (merged with existing data)."),
  order: z.number().optional().describe("For 'update': new sort order."),
  canonicalKey: z.string().max(200).optional().describe(
    "For 'update': semantic type (e.g. 'decision', 'tension'). Only changeable on draft/uncommitted entries.",
  ),
  autoPublish: z.boolean().optional().default(false).describe(
    "For 'update': only true when the user explicitly asks to publish. Default false = draft.",
  ),
  changeNote: z.string().max(2000).optional().describe(
    "For 'update': short human-readable rationale for WHY this change was made. Surfaces in activity feed.",
  ),
  sourceRef: z.string().max(2000).optional().describe(
    "For 'update': URI or path of the source document backing this entry. Write-once.",
  ),
  sourceExcerpt: z.string().max(5000).optional().describe(
    "For 'update': verbatim excerpt from the source backing this entry's claims. Write-once.",
  ),
  steeringOverrideReason: z.string().max(2000).optional().describe(
    "For 'update'/'commit': typed override (≥12 chars) that clears a steering coherency block.",
  ),
  coherencyAcknowledgement: coherencyAcknowledgementFlatSchema.optional().describe(
    "For 'update'/'commit': explicit response to a coherency challenge (standard/strict workspace modes).",
  ),
  preview: z.boolean().optional().describe(
    "For 'commit': if true, validates the accept without writing — returns what would happen.",
  ),
  toCollection: z.string().max(200).optional().describe("For 'move': target collection slug, e.g. 'decisions', 'architecture'."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion, built by reusing the
// existing single-purpose schemas (Code Integrity — the system already knows
// these shapes; `.extend()` adds only the discriminant). This is what actually
// enforces per-action semantics (e.g. `update`'s strict status union vs.
// `list`'s free-text filter) — the flat schema above never does.
const entriesListVariant = z.object({
  action: z.literal("list"),
  collection: z.string().max(200).optional(),
  status: z.string().max(200).optional(),
  tag: z.string().max(200).optional(),
  label: z.string().max(200).optional(),
});
const entriesGetVariant = z.object({
  action: z.literal("get"),
  entryId: z.string().max(200),
});
const entriesBatchVariant = z.object({
  action: z.literal("batch"),
  entryIds: z.array(z.string().max(200)).min(1).max(20),
});
const entriesSearchVariant = z.object({
  action: z.literal("search"),
  query: z.string().min(2).max(500),
  collection: z.string().max(200).optional(),
  status: entriesStatusFilterSchema.optional(), // PR #660 R1 Zqh1K: string|array.
});
const entriesUpdateVariant = updateEntrySchema.extend({ action: z.literal("update") });
const entriesCommitVariant = commitEntrySchema.extend({ action: z.literal("commit") });
const entriesHistoryVariant = getHistorySchema.extend({ action: z.literal("history") });
const entriesMoveVariant = z.object({
  action: z.literal("move"),
  entryId: z.string().max(200),
  toCollection: z.string().max(200),
});
const entriesVerifyVariant = verifyEntrySchema.extend({ action: z.literal("verify") });

export const entriesActionUnion = z.discriminatedUnion("action", [
  entriesListVariant,
  entriesGetVariant,
  entriesBatchVariant,
  entriesSearchVariant,
  entriesUpdateVariant,
  entriesCommitVariant,
  entriesHistoryVariant,
  entriesMoveVariant,
  entriesVerifyVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const ENTRIES_ACTION_SPECS: Record<EntriesAction, ActionParamSpec> = {
  list: { params: ["collection", "status", "tag", "label"], description: "Browse entries; all params optional." },
  get: { params: ["entryId"], description: "Fetch one entry by ID." },
  batch: { params: ["entryIds"], description: "Fetch up to 20 entries by ID." },
  search: { params: ["query", "collection", "status"], description: "Full-text search; query is required (min 2 chars)." },
  update: { params: ["entryId", "name", "status", "workflowStatus", "data", "order", "canonicalKey", "autoPublish", "changeNote", "sourceRef", "sourceExcerpt", "steeringOverrideReason", "coherencyAcknowledgement"], description: "entryId is required; every other field is optional." },
  commit: { params: ["entryId", "preview", "steeringOverrideReason", "coherencyAcknowledgement"], description: "entryId is required." },
  history: { params: ["entryId"], description: "entryId is required." },
  move: { params: ["entryId", "toCollection"], description: "Both entryId and toCollection are required." },
  verify: { params: ["entryId"], description: "entryId is required." },
};

export const entriesGetOutputSchema = z.object({
  entryId: z.string(),
  name: z.string(),
  collection: z.string(),
  status: z.string(),
  capturedAt: z.number().optional(),
  origin: z.string().optional(),
  originDetail: z.string().optional(),
  verificationStatus: z.string().optional(),
  // Attestation-model finding (PR #341 review): the server's honest verifier label and
  // derived attestation strength/basis — connectors NEVER re-derive strength (spec §5),
  // they only render what chain.getEntry ships. Mirrors packages/cli EntryFromApi.
  //
  // The literal set below is a hand-kept WIRE-SHAPE mirror of kernel/quality/
  // attestation.ts's AttestationStrength SSOT (allowlisted in Check D —
  // scripts/check-collection-ssot.mjs's CHECK_D_ALLOWLIST — with the full
  // reasoning for why this can't just import that module). Update both together.
  verifiedBy: z.string().optional(),
  attestation: z.object({
    strength: z.enum(["human-direct", "delegated", "system", "unattested"]),
    basis: z.string().optional(),
  }).optional(),
  sourceRef: z.string().optional(),
  sourceExcerpt: z.string().optional(),
  why: z.string().optional(),
  // TEN-2191: quality of the captured WHY — 'rationale' | 'missing' | 'restated'.
  whyQuality: z.enum(["rationale", "missing", "restated"]).optional(),
  data: z.record(z.unknown()).optional(),
  relations: z.array(z.object({
    entryId: z.string().optional(),
    name: z.string(),
    type: z.string(),
    direction: z.string(),
  })).optional(),
  labels: z.array(z.string()).optional(),
}).passthrough();

export const entriesListOutputSchema = z.object({
  entries: z.array(z.object({
    entryId: z.string(),
    name: z.string(),
    collection: z.string(),
    status: z.string(),
  })),
  total: z.number(),
});

export const entriesSearchOutputSchema = z.object({
  results: z.array(z.object({
    entryId: z.string(),
    name: z.string(),
    collection: z.string(),
    status: z.string(),
    score: z.number().optional(),
  })),
  total: z.number(),
  query: z.string(),
});

export const entriesBatchOutputSchema = z.object({
  entries: z.array(z.object({
    entryId: z.string(),
    name: z.string(),
    collection: z.string(),
    status: z.string(),
    capturedAt: z.number().optional(),
    origin: z.string().optional(),
    originDetail: z.string().optional(),
    verificationStatus: z.string().optional(),
    // Attestation-model finding (PR #341 review): mirror entriesGetOutputSchema — batch
    // entries now carry the same honest verifiedBy/attestation fields. Literal set is
    // the same hand-kept SSOT mirror — see entriesGetOutputSchema's attestation
    // comment above for the full Check D / kernel-import reasoning.
    verifiedBy: z.string().optional(),
    attestation: z.object({
      strength: z.enum(["human-direct", "delegated", "system", "unattested"]),
      basis: z.string().optional(),
    }).optional(),
    sourceRef: z.string().optional(),
    sourceExcerpt: z.string().optional(),
    // TEN-2191: mirror entriesGetOutputSchema — batch entries now carry why/whyQuality.
    why: z.string().optional(),
    whyQuality: z.enum(["rationale", "missing", "restated"]).optional(),
    data: z.record(z.unknown()).optional(),
  }).passthrough()),
  total: z.number(),
});

export function registerEntriesTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched by dispatchDiscriminated
  // via entriesActionUnion. A discriminatedUnion failure at this point is
  // ALWAYS "valid action, wrong param combination or per-action enum
  // violation" (§4 division of labor) — the registered flat layer already
  // rejected an unknown action or a wrong param TYPE before the handler runs.
  const entriesHandlers: Record<EntriesAction, (data: any) => Promise<ToolResult>> = {
    list: (data) => handleList(data.collection, data.status, data.tag, data.label),
    get: (data) => handleGet(data.entryId),
    batch: (data) => handleBatch(data.entryIds),
    search: (data) => handleSearch(server, data.query, data.collection, data.status),
    // update/commit reuse knowledge.ts's extracted handlers — "entries" routes
    // `next` at itself (toolName="entries") instead of the legacy tool names.
    update: (data) => handleUpdateEntry(data, "entries"),
    commit: (data) => handleCommitEntry(data, "entries"),
    history: (data) => handleGetHistory(data),
    move: (data) => handleMoveEntry(data.entryId, data.toCollection),
    verify: (data) => handleVerifyEntry(data),
  };

  server.registerTool(
    "entries",
    {
      title: "Entries",
      description:
        "Read and manage entries on the Chain. One tool for the entry lifecycle.\n\n" +
        "- **list**: Browse entries with optional filters (collection, status, tag, label). Use collections action=list first to discover slugs.\n" +
        "- **get**: Fetch a single entry by ID — full record with data, labels, relations, history.\n" +
        "- **batch**: Fetch multiple entries (max 20) in one call. Same shape as get per entry.\n" +
        "- **search**: Full-text search across entries. Scope by collection or filter by status.\n" +
        "- **update**: Change fields on an existing entry (draft by default — never autoPublish without explicit user confirmation).\n" +
        "- **commit**: Accept a draft entry onto the Chain (SSOT). Runs the contradiction preflight first.\n" +
        "- **history**: Audit trail for an entry.\n" +
        "- **move**: Reclassify an entry to a different collection.\n" +
        "- **verify**: Mark an entry as verified (lightweight, no codebase scan — see `quality action=verify-chain` for that).\n\n" +
        "Use `entries action=get entryId=\"...\"` to fetch one. Use `entries action=search query=\"...\"` to discover.",
      inputSchema: entriesSchema,
      // Mixed read/write noun (§3 R3): can't be marked read-only once update/commit/move
      // live here too — accepted for the 11-tool default (§9 R3).
      annotations: { readOnlyHint: false, destructiveHint: true, idempotentHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(entriesSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "entries", action }, () =>
        dispatchDiscriminated("entries", entriesActionUnion, parsed.data, ENTRIES_ACTION_SPECS, entriesHandlers),
      );
    }),
  );
}

async function handleGet(entryId: string) {
  const entry = await kernelQuery<unknown>("chain.getEntry", { entryId });

  if (!entry) {
    return notFoundResult(entryId, `Entry '${entryId}' not found. Try search to find the right ID.`);
  }

  const e = entry as Record<string, unknown>;
  const epistemic = deriveEpistemicStatus(toEpistemicInput(e));

  const lines: string[] = [
    `## Get Result`,
    "",
    `# ${e.entryId ? `${e.entryId}: ` : ""}${e.name}`,
    "",
    `**Status:** ${e.status}${e.workflowStatus ? ` / ${e.workflowStatus}` : ""}`,
    `**Type:** ${e.canonicalKey ?? "untyped"}`,
  ];

  // BET-240 S6: surface trust/provenance fields when present
  if (typeof e.capturedAt === "number") lines.push(`**Captured at:** ${new Date(e.capturedAt).toISOString()}`);
  if (e.origin) {
    const detail = e.originDetail ? ` (${e.originDetail})` : "";
    lines.push(`**Origin:** ${e.origin}${detail}`);
  }
  const verificationLine = renderVerificationLine(e);
  if (verificationLine) lines.push(verificationLine);
  if (e.sourceRef) lines.push(`**Source ref:** ${e.sourceRef}`);
  if (e.sourceExcerpt) lines.push(`**Source excerpt:** ${e.sourceExcerpt}`);

  if (epistemic) {
    lines.push(formatEpistemicLine(epistemic));
  }

  // TEN-2191 (honesty patch): self-announcing WHY-quality line for active governance
  // entries. whyQuality is rationale-only (no description fallback) so a missing or
  // restated rationale is surfaced rather than masked. Mirrors the CLI `pb get` render.
  const { line: whyLine, why, whySourceKey } = renderWhyLine(e);
  if (whyLine !== null) lines.push(whyLine);

  const visibleData = sanitizeEntryData(e.data);
  // Suppress whySourceKey (the field now surfaced as top-level `why`) from BOTH the
  // text Data block and the structured payload — this is lossless because `why`
  // carries the full untruncated value from extractWhy (get never caps it), so
  // removing the source field from `data` loses no information. Omitting it from
  // both surfaces restores true STD-218 text/structured parity and removes the
  // bloat that commit 8f3b1664 left in the structured payload. The whySourceKey is
  // the kernel-resolved field ('rationale' for most governance, 'description' for
  // architecture notes) — see renderWhyLine for derivation. (Codex finding, TEN-2191.)
  const filteredData = visibleData && whySourceKey
    ? (Object.keys(visibleData).filter((k) => k !== whySourceKey).length > 0
        ? Object.fromEntries(Object.entries(visibleData).filter(([k]) => k !== whySourceKey))
        : undefined)
    : visibleData;
  if (filteredData) {
    lines.push("");
    for (const [key, val] of Object.entries(filteredData)) {
      const display = typeof val === "string" ? val : JSON.stringify(val);
      lines.push(`**${key}:** ${display}`);
    }
  }

  if (Array.isArray(e.tags) && e.tags.length > 0) {
    lines.push("", `**Tags:** ${(e.tags as string[]).join(", ")}`);
  }

  if (Array.isArray(e.labels) && (e.labels as unknown[]).length > 0) {
    const labels = e.labels as { slug?: string; name?: string }[];
    lines.push("", `**Labels:** ${labels.map((l) => `\`${l.slug ?? l.name}\``).join(", ")}`);
  }

  if (Array.isArray(e.relations) && (e.relations as unknown[]).length > 0) {
    lines.push("", "## Relations");
    for (const r of e.relations as { direction?: string; type?: string; otherEntryId?: string; otherName?: string }[]) {
      const arrow = r.direction === "outgoing" ? "\u2192" : "\u2190";
      const other = r.otherEntryId ? `${r.otherEntryId}: ${r.otherName}` : (r.otherName ?? "unknown");
      lines.push(`- ${arrow} **${r.type}** ${other}`);
    }
  }

  if (Array.isArray(e.history) && (e.history as unknown[]).length > 0) {
    lines.push("", "## History (last 10)");
    // WP-D (attestation spec §7.2): legacyQualifier is server-derived — it renders beside a
    // legacy 'verified' event so the append-only ledger sentence stops lying. Never re-derived here.
    for (const h of (e.history as { timestamp: string; event: string; changedBy?: string; legacyQualifier?: string }[]).slice(-10)) {
      const date = new Date(h.timestamp).toISOString().split("T")[0];
      const qualifier = h.legacyQualifier ? ` [${h.legacyQualifier}]` : "";
      lines.push(`- ${date}: ${h.event}${h.changedBy ? ` _(${h.changedBy})_` : ""}${qualifier}`);
    }
  }

  const entryData = {
    entryId: String(e.entryId ?? ""),
    name: String(e.name ?? ""),
    collection: String(e.canonicalKey ?? ""),
    status: String(e.status ?? ""),
    ...(typeof e.capturedAt === "number" ? { capturedAt: e.capturedAt } : {}),
    ...(e.workflowStatus ? { workflowStatus: String(e.workflowStatus) } : {}),
    ...(epistemic ? { epistemicStatus: epistemic } : {}),
    // BET-240 S6: trust/provenance fields
    ...(e.origin ? { origin: String(e.origin) } : {}),
    ...(e.originDetail ? { originDetail: String(e.originDetail) } : {}),
    ...(e.verificationStatus ? { verificationStatus: String(e.verificationStatus) } : {}),
    // Attestation-model finding (PR #341 review): forward the server's honest verifier
    // label + derived attestation strength/basis on the structured payload too — the
    // agent surface must see the same fields the text render displays (spec §5).
    ...(typeof e.verifiedBy === "string" ? { verifiedBy: e.verifiedBy } : {}),
    ...(e.attestation && typeof e.attestation === "object" ? { attestation: e.attestation as { strength: string; basis?: string } } : {}),
    ...(e.sourceRef ? { sourceRef: String(e.sourceRef) } : {}),
    ...(e.sourceExcerpt ? { sourceExcerpt: String(e.sourceExcerpt) } : {}),
    // TEN-2191: surface the WHY and its quality in the structured payload too.
    ...(why.length > 0 ? { why } : {}),
    ...(e.whyQuality ? { whyQuality: String(e.whyQuality) } : {}),
    entries: [{ entryId: e.entryId, name: e.name, status: e.status, ...(e.workflowStatus ? { workflowStatus: e.workflowStatus } : {}), collectionName: String((e as { collectionName?: string }).collectionName ?? e.canonicalKey ?? "—") }],
    // Structured payload uses the same filteredData as the text render — the
    // WHY-source field (rationale or description) is intentionally omitted from
    // `data` because its full value is carried in the top-level `why` field
    // (no information loss; `why` is the complete untruncated extractWhy result).
    // This restores true STD-218 text/structured parity and removes the duplication
    // that commit 8f3b1664 left in the structured payload. (Codex finding, TEN-2191.)
    ...(filteredData ? { data: filteredData } : {}),
    ...(Array.isArray(e.relations) && (e.relations as unknown[]).length > 0
      ? {
          relations: (e.relations as { direction?: string; type?: string; otherEntryId?: string; otherName?: string }[]).map((r) => ({
            ...(r.otherEntryId ? { entryId: r.otherEntryId } : {}),
            name: r.otherName ?? "unknown",
            type: r.type ?? "unknown",
            direction: r.direction ?? "unknown",
          })),
        }
      : {}),
    ...(Array.isArray(e.labels) && (e.labels as unknown[]).length > 0
      ? { labels: (e.labels as { slug?: string; name?: string }[]).map((l) => l.slug ?? l.name ?? "") }
      : {}),
  };

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Found ${entryId}: ${String(e.name)} [${String(e.canonicalKey ?? "")}].`,
      entryData,
      [
        { tool: "relations", description: "Explore connections", parameters: { action: "suggest", entryId } },
        { tool: "context", description: "Gather related context", parameters: { action: "gather", entryId } },
      ],
    ),
  };
}

async function handleBatch(entryIds: string[]) {
  const results = await kernelQuery<GatewayRouteReturnByName<"chain.batchGetEntries">>("chain.batchGetEntries", { entryIds });

  const lines: string[] = [`## Batch Result`, "", `# Batch Get (${results.length} requested)`, ""];

  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    const requestedId = entryIds[i];
    if (r.error) {
      lines.push(`## ${requestedId}\n\n**Error:** ${r.error}\n`);
      continue;
    }
    const entry = r.entry as Record<string, unknown>;
    lines.push(`## ${entry.entryId ? `${entry.entryId}: ` : ""}${entry.name}`);
    lines.push("");
    lines.push(`**Status:** ${entry.status}${entry.workflowStatus ? ` / ${entry.workflowStatus}` : ""}`);
    lines.push(`**Type:** ${entry.canonicalKey ?? "untyped"}`);
    if (typeof entry.capturedAt === "number") {
      lines.push(`**Captured at:** ${new Date(entry.capturedAt).toISOString()}`);
    }
    if (entry.origin) {
      const detail = entry.originDetail ? ` (${entry.originDetail})` : "";
      lines.push(`**Origin:** ${entry.origin}${detail}`);
    }
    const entryVerificationLine = renderVerificationLine(entry);
    if (entryVerificationLine) lines.push(entryVerificationLine);
    if (entry.sourceRef) {
      lines.push(`**Source ref:** ${entry.sourceRef}`);
    }
    if (entry.sourceExcerpt) {
      lines.push(`**Source excerpt:** ${entry.sourceExcerpt}`);
    }

    // TEN-2191 (STD-218 parity): mirror handleGet's WHY line + source-key suppression.
    // renderWhyLine returns the self-announcing line, the canonical `why` text, and the
    // data key whose value is now surfaced as Why (so we can suppress it from the Data
    // block — lossless because `why` carries the full value). Mirrors handleGet exactly.
    const { line: whyLine, whySourceKey } = renderWhyLine(entry);
    if (whyLine !== null) lines.push(whyLine);

    const visibleData = sanitizeEntryData(entry.data);
    // Build filteredData: exclude the why-source key from both text and structured output.
    // Same inline logic as handleGet (~lines 278-282) — see there for the rationale.
    const filteredData = visibleData && whySourceKey
      ? (Object.keys(visibleData).filter((k) => k !== whySourceKey).length > 0
          ? Object.fromEntries(Object.entries(visibleData).filter(([k]) => k !== whySourceKey))
          : undefined)
      : visibleData;
    if (filteredData) {
      lines.push("");
      for (const [key, val] of Object.entries(filteredData)) {
        const display = typeof val === "string" ? val : JSON.stringify(val);
        lines.push(`**${key}:** ${display}`);
      }
    }

    if (Array.isArray(entry.tags) && entry.tags.length > 0) {
      lines.push("", `**Tags:** ${(entry.tags as string[]).join(", ")}`);
    }

    if (Array.isArray(entry.labels) && (entry.labels as unknown[]).length > 0) {
      const labels = entry.labels as { slug?: string; name?: string }[];
      lines.push("", `**Labels:** ${labels.map((l) => `\`${l.slug ?? l.name}\``).join(", ")}`);
    }

    if (Array.isArray(entry.relations) && (entry.relations as unknown[]).length > 0) {
      lines.push("", "**Relations:**");
      for (const rel of entry.relations as { direction?: string; type?: string; otherEntryId?: string; otherName?: string }[]) {
        const dir = rel.direction === "outgoing" ? "→" : "←";
        const other = rel.otherEntryId ?? rel.otherName ?? "?";
        lines.push(`- ${dir} [${rel.type}] ${other}`);
      }
    }

    if (Array.isArray(entry.history) && (entry.history as unknown[]).length > 0) {
      lines.push("", "**History (last 5):**");
      for (const h of (entry.history as { timestamp: string; event: string }[]).slice(-5)) {
        const date = new Date(h.timestamp).toISOString().split("T")[0];
        lines.push(`- ${date}: ${h.event}`);
      }
    }

    lines.push("");
  }

  const structuredEntries = results
    .filter((r) => !r.error && r.entry)
    .map((r) => {
      const entry = r.entry as Record<string, unknown>;
      // TEN-2191 (STD-218 parity): same conditional-spread as handleGet structured payload.
      // renderWhyLine is pure so re-calling it here is safe (no side-effects).
      const { why: entryWhy, whySourceKey: entryWhySourceKey } = renderWhyLine(entry);
      const entryVisibleData = sanitizeEntryData(entry.data);
      const entryFilteredData = entryVisibleData && entryWhySourceKey
        ? (Object.keys(entryVisibleData).filter((k) => k !== entryWhySourceKey).length > 0
            ? Object.fromEntries(Object.entries(entryVisibleData).filter(([k]) => k !== entryWhySourceKey))
            : undefined)
        : entryVisibleData;
      return {
        entryId: String(entry.entryId ?? ""),
        name: String(entry.name ?? ""),
        collection: String(entry.canonicalKey ?? ""),
        status: String(entry.status ?? ""),
        ...(typeof entry.capturedAt === "number" ? { capturedAt: entry.capturedAt } : {}),
        ...(entry.origin ? { origin: String(entry.origin) } : {}),
        ...(entry.originDetail ? { originDetail: String(entry.originDetail) } : {}),
        ...(entry.verificationStatus ? { verificationStatus: String(entry.verificationStatus) } : {}),
        // Attestation-model finding (PR #341 review): mirror handleGet — forward the
        // honest verifier label + derived attestation on batch's structured payload too.
        ...(typeof entry.verifiedBy === "string" ? { verifiedBy: entry.verifiedBy } : {}),
        ...(entry.attestation && typeof entry.attestation === "object" ? { attestation: entry.attestation as { strength: string; basis?: string } } : {}),
        ...(entry.sourceRef ? { sourceRef: String(entry.sourceRef) } : {}),
        ...(entry.sourceExcerpt ? { sourceExcerpt: String(entry.sourceExcerpt) } : {}),
        ...(entry.workflowStatus ? { workflowStatus: String(entry.workflowStatus) } : {}),
        // TEN-2191: surface why/whyQuality in structured payload — mirrors handleGet.
        ...(entryWhy.length > 0 ? { why: entryWhy } : {}),
        ...(entry.whyQuality ? { whyQuality: String(entry.whyQuality) } : {}),
        ...(entryFilteredData ? { data: entryFilteredData } : {}),
      };
    });

  const total = structuredEntries.length;
  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(`Fetched ${total} entries in batch.`, { entries: structuredEntries, total }),
  };
}

async function handleList(collection?: string, status?: string, tag?: string, label?: string) {
  let entries: unknown[];

  if (label) {
    entries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntriesByLabel">>("chain.listEntriesByLabel", { labelSlug: label });
    if (status) {
      entries = (entries as { status?: string }[]).filter((e) => e.status === status);
    }
  } else {
    entries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", {
      collectionSlug: collection,
      status,
      tag,
    });
  }

  if (entries.length === 0) {
    return {
      content: [{ type: "text" as const, text: "No entries match the given filters." }],
      structuredContent: success(
        "No entries match the given filters.",
        { entries: [], total: 0 },
        [{ tool: "collections", description: "List collections", parameters: { action: "list" } }],
      ),
    };
  }

  const formatted = (entries as { entryId?: string; name?: string; status?: string; workflowStatus?: string; data?: unknown }[])
    .map((e) => {
      const id = e.entryId ? `**${e.entryId}:** ` : "";
      const statusDisplay = e.workflowStatus ? `${e.status} / ${e.workflowStatus}` : e.status;
      const visibleData = sanitizeEntryData(e.data);
      const dataPreview = visibleData
        ? Object.entries(visibleData)
            .slice(0, 4)
            .map(([k, v]) => `  ${k}: ${typeof v === "string" ? v.substring(0, 120) : JSON.stringify(v)}`)
            .join("\n")
        : "";
      return `- ${id}${e.name} \`${statusDisplay}\`${dataPreview ? `\n${dataPreview}` : ""}`;
    })
    .join("\n\n");

  const scope = collection ? ` in \`${collection}\`` : "";

  const structuredEntries = (entries as { entryId?: string; name?: string; status?: string; workflowStatus?: string; canonicalKey?: string }[]).map((e) => ({
    entryId: String(e.entryId ?? ""),
    name: String(e.name ?? ""),
    collection: String(e.canonicalKey ?? collection ?? ""),
    status: String(e.status ?? ""),
    ...(e.workflowStatus ? { workflowStatus: e.workflowStatus } : {}),
  }));

  const total = structuredEntries.length;
  return {
    content: [{ type: "text" as const, text: `## List Result\n\n# Entries${scope} (${entries.length})\n\n${formatted}` }],
    structuredContent: success(`Found ${total} entries${scope}.`, { entries: structuredEntries, total }),
  };
}

async function handleSearch(
  server: McpServer,
  query: string,
  collection?: string,
  status?: string | string[],
) {
  const scope = collection ? ` in \`${collection}\`` : "";
  await server.sendLoggingMessage({
    level: "info",
    data: `Searching${scope} for "${query}"...`,
    logger: "product-os",
  });

  const [results, collections] = await Promise.all([
    kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query, collectionSlug: collection, status }),
    kernelQuery<unknown[]>("chain.listCollections"),
  ]);

  if (results.length === 0) {
    const recorded = recordGap({ query, tool: "entries", action: "search", gapType: "search_zero", collectionScope: collection });
    if (recorded) {
      getWorkspaceId()
        .then((wsId) => trackKnowledgeGap(wsId, { query, tool: "entries", action: "search", gap_type: "search_zero", collection_scope: collection }))
        .catch(() => {});
    }

    return {
      content: [{
        type: "text" as const,
        text: `"${query}" is not yet on the Chain${scope}. Try a broader search or use \`collections action=list\` to browse available knowledge.`,
      }],
      structuredContent: success(
        `'${query}' is not yet on the Chain${scope}.`,
        { entries: [], total: 0, query },
        [
          { tool: "entries", description: "Try a broader search", parameters: { action: "search", query: query.split(" ")[0] } },
          { tool: "collections", description: "Browse available collections", parameters: { action: "list" } },
        ],
      ),
    };
  }

  const collMap = new Map<string, { name: string; slug: string }>();
  for (const c of collections as { _id: string; name: string; slug: string }[]) {
    collMap.set(c._id, { name: c.name, slug: c.slug });
  }

  const countsBySlug = new Map<string, number>();
  for (const e of results as { collectionId: string }[]) {
    const col = collMap.get(e.collectionId);
    const slug = col?.slug ?? "unknown";
    countsBySlug.set(slug, (countsBySlug.get(slug) ?? 0) + 1);
  }
  const collSummary = [...countsBySlug.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([slug, count]) => `${count} ${slug}`)
    .join(", ");

  const formatted = (results as {
    entryId?: string;
    name?: string;
    status?: string;
    workflowStatus?: string;
    collectionId?: string;
    canonicalKey?: string;
    data?: unknown;
  }[])
    .map((e) => {
      const id = e.entryId ? `**${e.entryId}:** ` : "";
      const statusDisplay = e.workflowStatus ? `${e.status} / ${e.workflowStatus}` : e.status;
      const col = collMap.get(e.collectionId ?? "");
      const colTag = col ? ` [${col.slug}]` : "";
      const typeTag = e.canonicalKey ? ` (${e.canonicalKey})` : "";
      const desc = extractPreview(e.data, 150);
      const preview = desc ? `\n  ${desc}` : "";
      return `- ${id}${e.name} \`${statusDisplay}\`${colTag}${typeTag}${preview}`;
    })
    .join("\n");

  const header = `## Search Result\n\n# Search Results for "${query}"${scope} (${results.length} match${results.length === 1 ? "" : "es"})\n\n**By collection:** ${collSummary}`;
  const footer = `_Tip: Use \`collection\` param to scope search. Use entries action=get with an entry ID for full details._`;

  const structuredResults = (results as {
    entryId?: string;
    name?: string;
    status?: string;
    workflowStatus?: string;
    collectionId?: string;
    score?: number;
  }[]).map((e) => ({
    entryId: String(e.entryId ?? ""),
    name: String(e.name ?? ""),
    collection: collMap.get(e.collectionId ?? "")?.slug ?? "unknown",
    status: String(e.status ?? ""),
    ...(e.workflowStatus ? { workflowStatus: e.workflowStatus } : {}),
    ...(e.score != null ? { score: e.score } : {}),
  }));

  const entriesForView = structuredResults.map((e) => ({
    entryId: e.entryId,
    name: e.name,
    status: e.status,
    collectionName: e.collection,
  }));

  const total = structuredResults.length;
  return {
    content: [{ type: "text" as const, text: `${header}\n\n${formatted}\n\n${footer}` }],
    structuredContent: success(`Found ${total} entries matching '${query}'.`, {
      results: structuredResults,
      entries: entriesForView,
      total,
      query,
    }),
  };
}
